/**
 * alfer-Insights: Spaltenverteilung des Teaser-Mosaiks.
 *
 * Das PHP-Template gibt die Karten FLACH in CMS-Reihenfolge aus (korrekte
 * Lesereihenfolge, ohne JS eine saubere einspaltige Liste). Dieses Modul
 * verteilt sie im Rundlauf auf N Spaltencontainer - genau die Verteilung, die
 * im Screendesign (Figma 438:1628) das versetzte Mosaik ergibt:
 *
 *   Karte 1 -> Spalte 1, Karte 2 -> Spalte 2, Karte 3 -> Spalte 3,
 *   Karte 4 -> Spalte 1, ...
 *
 * Die Spaltenanzahl kommt aus der CSS-Custom-Property --insights-columns, wird
 * also allein im SCSS je Breakpoint gepflegt. Den vertikalen Versatz der
 * Aussenspalten setzt ebenfalls das CSS (.is-distributed .insights__col).
 */

const COLUMN_CLASS = 'insights__col';
const DISTRIBUTED_CLASS = 'is-distributed';

/**
 * Gewuenschte Spaltenanzahl aus dem CSS lesen (Fallback: 1).
 */
function getColumnCount(grid) {
  const raw = getComputedStyle(grid).getPropertyValue('--insights-columns');
  const count = parseInt(raw, 10);

  return Number.isFinite(count) && count > 0 ? count : 1;
}

/**
 * Karten in ihrer urspruenglichen CMS-Reihenfolge liefern.
 *
 * Wichtig: Sobald die Karten einmal auf Spalten verteilt sind, entspricht die
 * DOM-Reihenfolge nicht mehr der CMS-Reihenfolge (sie ist dann spaltenweise
 * sortiert). Ein erneutes Auslesen aus dem DOM wuerde die Reihenfolge bei jedem
 * Breakpoint-Wechsel weiter verwuerfeln. Deshalb wird die Ausgangsreihenfolge
 * beim ersten Zugriff einmalig festgehalten.
 */
function collectCards(grid) {
  if (!grid.insightsCards) {
    grid.insightsCards = Array.from(grid.querySelectorAll('.insights__card'));
  }

  return grid.insightsCards;
}

/**
 * Karten im Rundlauf auf `count` Spalten verteilen. Bei count === 1 werden die
 * Spaltencontainer wieder abgebaut, damit die flache Liste zurueckkommt.
 */
function distribute(grid, cards, count) {
  const fragment = document.createDocumentFragment();

  if (count <= 1) {
    cards.forEach((card) => fragment.appendChild(card));
    grid.classList.remove(DISTRIBUTED_CLASS);
  } else {
    const columns = [];

    for (let i = 0; i < count; i += 1) {
      const column = document.createElement('div');
      column.className = COLUMN_CLASS;
      columns.push(column);
      fragment.appendChild(column);
    }

    cards.forEach((card, index) => {
      columns[index % count].appendChild(card);
    });

    grid.classList.add(DISTRIBUTED_CLASS);
  }

  grid.replaceChildren(fragment);
}

/**
 * Neu verteilen, wenn sich die Spaltenanzahl geaendert hat. Ohne Aenderung
 * passiert nichts - so bleibt das Resize-Handling guenstig und die Karten
 * behalten ihren DOM-Zustand (z. B. laufende Bild-Transitions).
 */
function sync(grid) {
  const count = getColumnCount(grid);

  if (grid.dataset.insightsColumns === String(count)) {
    return;
  }

  grid.dataset.insightsColumns = String(count);
  distribute(grid, collectCards(grid), count);
}

export function initInsights() {
  // Nur die Preview/Teaser-Variante wird zum Mosaik verteilt. Die
  // Uebersichtsseite bleibt beim gleichmaessigen CSS-Kachelraster - das
  // skaliert mit beliebig vielen Eintraegen und braucht kein JS.
  const grids = document.querySelectorAll('.js-insights[data-insights-mosaic]');

  if (!grids.length) {
    return;
  }

  grids.forEach((grid) => {
    // Idempotenz: mehrfacher Aufruf darf nicht mehrfach beobachten.
    if (grid.dataset.insightsReady === 'true') {
      return;
    }
    grid.dataset.insightsReady = 'true';

    sync(grid);
  });

  if (window.__insightsResizeBound) {
    return;
  }
  window.__insightsResizeBound = true;

  let frame = 0;
  window.addEventListener('resize', () => {
    if (frame) {
      cancelAnimationFrame(frame);
    }
    frame = requestAnimationFrame(() => {
      frame = 0;
      document.querySelectorAll('.js-insights[data-insights-mosaic]').forEach(sync);
    });
  }, { passive: true });
}
